/**
 * A ficha preenchida corrigida e impressa, sobre o PostgREST — trilha T5a.
 *
 * Corrigir passa pela `alterar_respostas_da_ficha` da `0086`: as regras e o
 * motivo moram no banco, e a `0038` registra o que mudou.
 */

import { supabase } from '@/supabase';
import { atual } from '@/session/sessao';
import { cabecalhoDe, respostasLimpas } from './fichaImpressa';
import type { CabecalhoDaClinica } from './fichaImpressa';
import type { PortaDeDados } from './porta';
import type { CampoDeFicha, DadosDaClinica, FichaPreenchida, RespostaDaFicha, UnidadeDaClinica } from './tipos';

type MetodosDaFicha = Pick<PortaDeDados, 'alterarRespostasDaFicha' | 'cabecalhoDaClinica'>;

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

function clinicaId(): string {
    const id = atual()?.clinicaId;
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

function erro(mensagem: string, e: { message: string; code?: string } | null): void {
    if (!e) return;
    if (e.code === '42501') throw new Error('Corrigir ficha exige a permissão de atendimento.');
    if (e.code === 'P0001' || e.code === '22023') throw new Error(e.message);
    throw new Error(`${mensagem}: ${e.message}`);
}

function nomeEmbutido(v: unknown): string | undefined {
    const x = Array.isArray(v) ? v[0] : v;
    return x && typeof x === 'object' ? ((x as { nome?: string }).nome ?? undefined) : undefined;
}

const texto = (v: unknown) => (v === null || v === undefined ? '' : String(v));

export const fichaNoSupabase: MetodosDaFicha = {
    async alterarRespostasDaFicha(fichaId: string, respostas: Record<string, RespostaDaFicha>, motivo: string): Promise<FichaPreenchida> {
        const { error } = await cliente().rpc('alterar_respostas_da_ficha', {
            p_ficha: fichaId, p_respostas: respostasLimpas(respostas), p_motivo: motivo,
        });
        erro('Falha ao corrigir a ficha', error);

        const { data, error: erroDaLeitura } = await cliente()
            .from('fichas_preenchidas')
            .select('id, atendimento_id, contato_id, modelo_id, nome, respostas, definicao_usada, versao_do_modelo, criado_em, atualizado_em, membros!preenchida_por(nome)')
            .eq('id', fichaId)
            .single();
        erro('A ficha foi corrigida, mas não foi possível relê-la', erroDaLeitura);

        const f = data as unknown as Record<string, unknown>;
        const autor = nomeEmbutido(f.membros);
        return {
            id: texto(f.id),
            atendimentoId: texto(f.atendimento_id),
            contatoId: texto(f.contato_id),
            ...(f.modelo_id ? { modeloId: texto(f.modelo_id) } : {}),
            nome: texto(f.nome),
            respostas: (f.respostas as Record<string, RespostaDaFicha> | null) ?? {},
            ...(Array.isArray(f.definicao_usada) ? { definicaoUsada: f.definicao_usada as CampoDeFicha[] } : {}),
            ...(typeof f.versao_do_modelo === 'number' ? { versaoDoModelo: f.versao_do_modelo } : {}),
            ...(autor ? { preenchidaPor: autor } : {}),
            criadoEm: texto(f.criado_em),
            atualizadoEm: texto(f.atualizado_em),
        };
    },

    async cabecalhoDaClinica(): Promise<CabecalhoDaClinica> {
        const [clinicaResp, unidadesResp] = await Promise.all([
            cliente().from('clinicas').select('id, nome, razao_social, documento, tipo_pessoa, email, fuso, plano').eq('id', clinicaId()).single(),
            cliente().from('unidades').select('id, nome, telefone, email, cep, estado, cidade, bairro, rua, numero, complemento').is('arquivado_em', null).order('nome'),
        ]);
        erro('Falha ao ler os dados da clínica', clinicaResp.error);
        erro('Falha ao ler as unidades', unidadesResp.error);

        const c = clinicaResp.data as unknown as Record<string, unknown>;
        const clinica: DadosDaClinica = {
            id: texto(c.id), nome: texto(c.nome), razaoSocial: texto(c.razao_social), documento: texto(c.documento),
            tipoPessoa: c.tipo_pessoa === 'fisica' ? 'fisica' : 'juridica', email: texto(c.email), fuso: texto(c.fuso), plano: texto(c.plano),
        };
        const unidades = ((unidadesResp.data ?? []) as unknown as Record<string, unknown>[]).map((u): UnidadeDaClinica => ({
            id: texto(u.id), nome: texto(u.nome), telefone: texto(u.telefone), email: texto(u.email), cep: texto(u.cep),
            estado: texto(u.estado), cidade: texto(u.cidade), bairro: texto(u.bairro), rua: texto(u.rua), numero: texto(u.numero), complemento: texto(u.complemento),
        }));
        return cabecalhoDe(clinica, unidades);
    },
};
